/**
 * Communication Plan Integration
 */
import i18next from 'i18next';
import { toast } from 'sonner';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class CommunicationPlanIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchAll(params = {}) {
        try {
            const response = await this.service.getAll(params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async create(data) {
        try {
            const response = await this.service.create(data);
            toast.success(i18next.t('integrationToasts:communicationPlanCreated', {
                defaultValue: 'Communication plan created',
            }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async update(id, data) {
        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('integrationToasts:communicationPlanUpdated', {
                defaultValue: 'Communication plan updated',
            }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async delete(id) {
        try {
            await this.service.delete(id);
            toast.success(i18next.t('integrationToasts:communicationPlanDeleted', {
                defaultValue: 'Communication plan deleted',
            }));
            return { success: true };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async batchDelete(ids) {
        try {
            const response = await this.service.batchDelete(ids);
            toast.success(i18next.t('integrationToasts:communicationPlansDeleted', {
                defaultValue: 'Communication plans deleted',
            }));
            return { success: true, deletedCount: response.deletedCount };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async fetchPartyTemplate(params = {}) {
        try {
            const res = await this.service.getPartyTemplate(params);
            return { success: true, data: res.data ?? null };
        } catch {
            return { success: false, data: null };
        }
    }

    async fetchParty(planId) {
        if (!planId) return { success: false, data: null, partyTemplate: null };
        try {
            const res = await this.service.getParty(planId);
            return {
                success: res.success !== false,
                data: res.data ?? null,
                partyTemplate: res.partyTemplate ?? null,
            };
        } catch {
            return { success: false, data: null, partyTemplate: null };
        }
    }

    async syncParty(planId, payload) {
        try {
            const res = await this.service.syncParty(planId, payload);
            toast.success(i18next.t('integrationToasts:ownershipSaved', { defaultValue: 'Ownership saved' }));
            return { success: true, data: res.data };
        } catch (error) {
            const message = getIntegrationErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }
}
